import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { AUTH } from "@/constants/testIds";
import { getStoredReferralCode, clearStoredReferralCode } from "@/components/ReferralBanner";

export default function AuthPage() {
  const { setUser } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const ref = getStoredReferralCode();
      const res = mode === "login"
        ? await api.login({ email: form.email, password: form.password })
        : await api.register({ email: form.email, password: form.password, name: form.name, ref });
      if (mode === "register") clearStoredReferralCode();
      setUser(res.user);
      navigate("/profile");
    } catch (err) {
      setError(err?.response?.data?.detail || "Auth failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 pt-20 pb-24">
      <p className="eyebrow mb-4">№ 04</p>
      <h1 className="font-serif-display text-5xl mb-2">{mode === "login" ? t.auth.loginTitle : t.auth.registerTitle}</h1>
      <p className="text-[color:var(--ink-2)] mb-10">{t.tagline}</p>

      <button data-testid={AUTH.googleBtn} onClick={googleLogin} className="btn-outline w-full justify-center mb-10">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
        </svg>
        {t.auth.google}
      </button>

      <div className="flex items-center gap-4 mb-8">
        <div className="flex-1 h-px bg-[color:rgba(0,16,48,0.15)]" />
        <span className="text-xs tracking-[0.22em] uppercase text-[color:var(--ink-3)]">or</span>
        <div className="flex-1 h-px bg-[color:rgba(0,16,48,0.15)]" />
      </div>

      <form onSubmit={submit} className="space-y-6">
        {mode === "register" && (
          <div><label className="label-line">{t.auth.name}</label><input data-testid={AUTH.nameInput} required className="input-line" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
        )}
        <div><label className="label-line">{t.auth.email}</label><input data-testid={AUTH.emailInput} required type="email" className="input-line" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
        <div><label className="label-line">{t.auth.password}</label><input data-testid={AUTH.passwordInput} required type="password" minLength={6} className="input-line" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} /></div>
        {error && <p data-testid={AUTH.error} className="text-sm text-[color:var(--red)]">{error}</p>}
        <button data-testid={AUTH.submit} disabled={busy} className="btn-navy w-full justify-center">{busy ? "..." : (mode === "login" ? t.auth.login : t.auth.register)}</button>
      </form>

      <button data-testid={AUTH.toggle} onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-8 text-xs tracking-[0.22em] uppercase underline text-[color:var(--ink-2)]">
        {mode === "login" ? t.auth.switchToRegister : t.auth.switchToLogin}
      </button>
      {mode === "login" && (
        <div className="mt-4">
          <Link data-testid={AUTH.forgotLink} to="/forgot-password" className="text-xs tracking-[0.22em] uppercase underline text-[color:var(--ink-2)]">
            {t.auth.forgot}
          </Link>
        </div>
      )}
    </div>
  );
}
